// 2026-07-01 - FormuLearning - 관리자: 오퍼레이션 실행 내역 (이슈 #32)
// 파일 위치: frontend/src/features/admin/OperationLogList.tsx
// 어느 유저가 어느 프로젝트·스텝에서 어느 오퍼레이션을 실행했는지. payload 상세 없음.

import { useEffect, useState } from 'react'
import { listOperations, listOperationCatalog, listAdminUsers, listAdminProjects } from '../../api/admin'
import type { AdminOperationLog, OperationCatalogItem, AdminUserRef, AdminProjectRef } from '../../types/admin'
import { opLabel } from '../../types/admin'

export default function OperationLogList() {
  const [catalog, setCatalog] = useState<OperationCatalogItem[]>([])
  const [users, setUsers] = useState<AdminUserRef[]>([])
  const [projects, setProjects] = useState<AdminProjectRef[]>([])
  const [operation, setOperation] = useState('')
  const [userId, setUserId] = useState('')
  const [projectId, setProjectId] = useState('')
  const [rows, setRows] = useState<AdminOperationLog[]>([])
  const [cursor, setCursor] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    listOperationCatalog().then(setCatalog).catch(() => {})
    listAdminUsers().then(setUsers).catch(() => {})
    listAdminProjects().then(setProjects).catch(() => {})
  }, [])

  async function load(reset: boolean) {
    setLoading(true)
    try {
      const page = await listOperations({
        operation: operation || undefined,
        user_id: userId || undefined,
        project_id: projectId || undefined,
        cursor: reset ? undefined : cursor ?? undefined,
        limit: 50,
      })
      setRows((prev) => (reset ? page.items : [...prev, ...page.items]))
      setCursor(page.next_cursor)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load(true) /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [operation, userId, projectId])

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-text-secondary">
          Operation
          <select value={operation} onChange={(e) => setOperation(e.target.value)} className="rounded border bg-bg-input px-2 py-1 text-sm text-text-body" style={{ borderColor: 'var(--border-divider)' }}>
            <option value="">All</option>
            {catalog.map((c) => <option key={c.code} value={c.code}>{opLabel(c.code)}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-text-secondary">
          User
          <select value={userId} onChange={(e) => setUserId(e.target.value)} className="rounded border bg-bg-input px-2 py-1 text-sm text-text-body" style={{ borderColor: 'var(--border-divider)' }}>
            <option value="">All</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-text-secondary">
          Project
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="max-w-[220px] rounded border bg-bg-input px-2 py-1 text-sm text-text-body" style={{ borderColor: 'var(--border-divider)' }}>
            <option value="">All</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        {(operation || userId || projectId) && (
          <button
            onClick={() => { setOperation(''); setUserId(''); setProjectId('') }}
            className="rounded px-3 py-1 text-xs text-text-secondary hover:bg-bg-input hover:text-text-body"
            style={{ border: '1px solid var(--border-divider)' }}
          >
            Reset filters
          </button>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--border-divider)' }}>
        <table className="w-full text-left text-sm">
          <thead className="text-xs text-text-secondary" style={{ backgroundColor: 'var(--color-bg-panel)' }}>
            <tr>
              <th className="px-3 py-2 font-medium">Time</th>
              <th className="px-3 py-2 font-medium">User</th>
              <th className="px-3 py-2 font-medium">Project</th>
              <th className="px-3 py-2 font-medium">Step</th>
              <th className="px-3 py-2 font-medium">Operation</th>
              <th className="px-3 py-2 font-medium">Source</th>
              <th className="px-3 py-2 font-medium" title="이 실행이 유발한 외부 API 호출 수">API calls</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t" style={{ borderColor: 'var(--border-divider)' }}>
                <td className="px-3 py-2 text-text-secondary whitespace-nowrap">{new Date(r.started_at).toLocaleString()}</td>
                <td className="px-3 py-2 text-text-body" title={r.user_email ?? ''}>{r.user_name ?? '—'}</td>
                <td className="px-3 py-2 text-text-body max-w-[180px] truncate" title={r.project_name ?? ''}>{r.project_name ?? '—'}</td>
                <td className="px-3 py-2 text-text-secondary">{r.step ?? '—'}</td>
                <td className="px-3 py-2 text-text-body">{opLabel(r.operation)}</td>
                <td className="px-3 py-2 text-text-secondary">{r.source}</td>
                <td className="px-3 py-2 text-text-secondary">{r.api_calls > 0 ? r.api_calls : '—'}</td>
              </tr>
            ))}
            {rows.length === 0 && !loading && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-text-secondary">No operations match the filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3">
        {cursor != null && (
          <button onClick={() => load(false)} disabled={loading} className="rounded px-3 py-1.5 text-xs text-text-body hover:bg-bg-input" style={{ border: '1px solid var(--border-divider)' }}>
            {loading ? 'Loading…' : 'Load more'}
          </button>
        )}
        <span className="text-xs text-text-secondary">{rows.length} rows</span>
      </div>
    </div>
  )
}
